/* eslint-disable @typescript-eslint/no-empty-object-type */
import type { Fnode, rule } from "@protontech/fathom";
import type { Features, Inferrer } from "@protontech/ml-inference";

export type AnyRule = ReturnType<typeof rule>;

export type Coeff = [string, number];

export type PerceptronParams = {
  coeffs: Coeff[];
  bias: number;
};

export enum FormType {
  LOGIN = "login",
  NOOP = "noop",
  PASSWORD_CHANGE = "password-change",
  RECOVERY = "recovery",
  REGISTER = "register",
}

export enum FieldType {
  EMAIL = "email",
  IDENTITY = "identity",
  OTP = "otp",
  PASSWORD_CURRENT = "password",
  PASSWORD_NEW = "new-password",
  USERNAME = "username",
  USERNAME_HIDDEN = "username-hidden",
  CREDIT_CARD = "credit-card",
}

export enum CCFieldType {
  NAME = "cc:name",
  FIRSTNAME = "cc:firstname",
  LASTNAME = "cc:lastname",
  NUMBER = "cc:number",
  CSC = "cc:cvc",
  EXP = "cc:exp",
  EXP_YEAR = "cc:exp-year",
  EXP_MONTH = "cc:exp-month",
}

export enum IdentityFieldType {
  FULLNAME = "id:fullname",
  FIRSTNAME = "id:firstname",
  MIDDLENAME = "id:middlname",
  LASTNAME = "id:lastname",
  TELEPHONE = "id:tel",
  ADDRESS = "id:address",
  STATE = "id:state",
  CITY = "id:city",
  ZIPCODE = "id:zipcode",
  ORGANIZATION = "id:org",
  COUNTRY = "id:country",
  EMAIL = "id:email",
}

export const fieldTypes = Object.values(FieldType);
export const formTypes = Object.values(FormType);
export const ccFields = new Set<string>(Object.values(CCFieldType));
export const identityFields = new Set<string>(Object.values(IdentityFieldType));

export const fieldClasses = ["email", "otp", "password", "new-password", "username", "username-hidden"] as const;
export const formClasses = ["login", "password-change", "recovery", "register"] as const;
export const detectionClasses = [...fieldClasses, ...formClasses] as const;
export const abstractDetectionClasses = ["field", "form"] as const;

export type FormClass = (typeof formClasses)[number];
export type FieldClass = (typeof fieldClasses)[number];
export type DetectionClass = (typeof detectionClasses)[number];
export type HeuristicDetectionClass = (typeof abstractDetectionClasses)[number];

export type AbstractFeatures<T = unknown> = Record<string, AbstractFeature<T>>;

/** Base feature type parameterized by computed return type `T`. Declared as an
 * interface to lazily resolve the circular `AbstractFeatures` reference. */
export interface AbstractFeature<T = unknown> extends Feature<string, T, AbstractFeatures> {}

/** Maps each parent feature to its computed return type, keyed by the feature's runtime
 * `name` (via `as` key remapping) to match what topological resolution produces at runtime. */
export type InferParentComputeType<P extends AbstractFeatures> = { [K in keyof P as P[K]["name"]]: ReturnType<P[K]["compute"]> };
export type InferFeatureName<F extends AbstractFeature> = F extends Feature<infer N, unknown, AbstractFeatures> ? N : never;
export type InferFeatureReturnType<F extends AbstractFeature> = F extends Feature<string, infer R, AbstractFeatures> ? R : never;

export type Feature<N extends string, R, P extends AbstractFeatures> = {
  name: N;
  parents: P;
  compute(parents: InferParentComputeType<P>, fnode: Fnode): R;
  /** Marks features that should remain in the per-group output type but excluded
   * from `FeatureComputer.features` (and therefore from trained-model rules). Set
   * via the `feature(..., { private: true })` opts arg at declaration time. */
  private?: true;
};

export type ComputedFeatures<F extends AbstractFeatures> = { [K in keyof F as F[K]["name"]]: ReturnType<F[K]["compute"]> };

export interface FeatureComputer<F extends AbstractFeatures = AbstractFeatures> {
  compute(fnode: Fnode): ComputedFeatures<F>;
  /** Names of the non-private outputs. Iterated by the rules layer to build
   * one trained-model rule per feature. Derived once at module init. */
  features: string[];
}

export type ModelProviderRegistry = Record<ModelName, ModelProvider>;
export type ModelName = string;
export type ModelProvider = {
  [Klass in DetectionClass | HeuristicDetectionClass]: Klass extends HeuristicDetectionClass
    ? {
        featureComputer: FeatureComputer;
      }
    : {
        featureComputer: FeatureComputer;
        model: Inferrer<Features, number>;
      };
};
